import { atualizarContaContabilSchema, atualizarItemCifSchema, criarContaContabilSchema, criarItemCifSchema, type ApropriacaoEntrada, type ApropriacaoResponse, type ItemCifResponse } from '@triade/contracts';
import { apropriacaoParaEntrada } from '@/components/imobilizados/imobilizado-form';

/**
 * Formularios de Itens de CIF e de Contas contabeis. Strings de tela; o corpo sai pelos schemas do contrato.
 * Nenhuma conta aqui: a apropriacao e o resumo vem da API (e so exibidos).
 */

/** Destino do item: a apropriacao padrao (MANUAL, IGUAL ou CRITERIO) ou o atalho "vai direto para o centro". */
export type DestinoItem = 'apropriacao' | 'centro';

export interface ItemFormEntrada {
  nome: string;
  ativo: boolean;
  contaIds: string[];
  destino: DestinoItem;
  centroId: string;
}

export interface ErroCampoForm {
  campo: string | null;
  mensagem: string;
}

export function itemVazio(): ItemFormEntrada {
  return { nome: '', ativo: true, contaIds: [], destino: 'apropriacao', centroId: '' };
}

/** Registro da API para o formulario. O destino comeca em "apropriacao" (a API nao diz se veio do atalho). */
export function deItem(i: ItemCifResponse): ItemFormEntrada {
  return { nome: i.nome, ativo: i.ativo, contaIds: i.contas.map((c) => c.id), destino: 'apropriacao', centroId: '' };
}

/** Apropriacao padrao do item em formato de entrada (para o componente de apropriacao). */
export function apropriacaoInicial(a: ApropriacaoResponse | null | undefined): ApropriacaoEntrada | null {
  return apropriacaoParaEntrada(a ?? null);
}

/**
 * Monta o corpo do item. Criacao: nome, contas, e a apropriacao (ou o centro). Edicao: so o que mudou na tela
 * (`apropriacaoAlterada` = a apropriacao foi editada; sem ela, nao vai no corpo).
 * Valida com o schema do contrato; a primeira issue vira o erro do campo.
 */
export function montarCorpoItem(
  v: ItemFormEntrada,
  opcoes: { criacao: boolean; apropriacao: ApropriacaoEntrada | null; apropriacaoAlterada: boolean },
): { ok: true; corpo: Record<string, unknown> } | { ok: false; erro: ErroCampoForm } {
  const corpo: Record<string, unknown> = { nome: v.nome.trim(), contaIds: v.contaIds };
  if (!opcoes.criacao) corpo.ativo = v.ativo;

  if (v.destino === 'centro') {
    if (v.centroId === '') return { ok: false, erro: { campo: 'centroId', mensagem: 'Escolha o centro que recebe 100% do valor' } };
    corpo.centroId = v.centroId;
  } else if (opcoes.criacao) {
    if (opcoes.apropriacao) corpo.apropriacao = opcoes.apropriacao;
  } else if (opcoes.apropriacaoAlterada) {
    corpo.apropriacao = opcoes.apropriacao;
  }

  const r = opcoes.criacao ? criarItemCifSchema.safeParse(corpo) : atualizarItemCifSchema.safeParse(corpo);
  if (!r.success) {
    const i = r.error.issues[0];
    return { ok: false, erro: { campo: (i?.path[0] as string | undefined) ?? null, mensagem: i?.message ?? 'Dados inválidos' } };
  }
  return { ok: true, corpo: r.data as Record<string, unknown> };
}

/** Resumo de uma linha da lista (so texto). Os percentuais ja vem da API. */
export function resumoApropriacao(a: ApropriacaoResponse | null): string {
  if (!a) return 'Sem apropriação';
  if (a.erro) return a.erro.message;
  if (a.tipo === 'CRITERIO') return `Critério${a.criterioNome ? `: ${a.criterioNome}` : ''}`;
  if (a.tipo === 'IGUAL') return `Igual entre ${a.linhas.length} centro${a.linhas.length === 1 ? '' : 's'}`;
  return a.linhas.map((l) => l.nome).join(', ');
}

/* ---------- Contas contabeis ---------- */

export interface ContaFormEntrada {
  codigo: string;
  descricao: string;
  ativo: boolean;
}

export function contaVazia(): ContaFormEntrada {
  return { codigo: '', descricao: '', ativo: true };
}

export function deConta(c: { codigo: string; descricao: string; ativo: boolean }): ContaFormEntrada {
  return { codigo: c.codigo, descricao: c.descricao, ativo: c.ativo };
}

/** Criacao: codigo e descricao. Edicao: os campos (todos, com ativo). Valida com o schema do contrato. */
export function montarCorpoConta(v: ContaFormEntrada, criacao: boolean): { ok: true; corpo: Record<string, unknown> } | { ok: false; erro: ErroCampoForm } {
  const corpo: Record<string, unknown> = criacao ? { codigo: v.codigo.trim(), descricao: v.descricao.trim() } : { codigo: v.codigo.trim(), descricao: v.descricao.trim(), ativo: v.ativo };
  const r = criacao ? criarContaContabilSchema.safeParse(corpo) : atualizarContaContabilSchema.safeParse(corpo);
  if (!r.success) {
    const i = r.error.issues[0];
    return { ok: false, erro: { campo: (i?.path[0] as string | undefined) ?? null, mensagem: i?.message ?? 'Dados inválidos' } };
  }
  return { ok: true, corpo: r.data as Record<string, unknown> };
}
